import React, { useState } from "react";
import {
  DialogProps,
  DiagnosticsQueryParams,
  Severity,
  QueryType,
  FilterType,
  TimeRange,
  TurbineInfo,
} from "../../types/dialog-types";
import { getTigerResource } from "../../utils/tigerResource";
import LangDateInput from "../LangDateInput";
import { validateEndAfterStart } from "../../utils/tigerTime";
import { fetchTurbineList, fetchTurbineStates } from "../../api/channelSelection";
import { acrylicStyles, acrylicTokens, acrylicDialogSurface } from "../../theme/acrylic";

// Resource ids for the value-keyed option lists (the array entries double as
// internal values, so display text is looked up via these maps — mirrors the
// SetWindowText calls in the C++ TigerDiag property pages).
const SEVERITY_RES: Record<Severity, string> = {
  Trip: "TIGER_QUERY_SEV_TRIP",
  Critical: "TIGER_QUERY_SEV_CRIT",
  Fault: "TIGER_QUERY_SEV_FAULT",
  Warning: "TIGER_QUERY_SEV_WARN",
  Event: "TIGER_QUERY_SEV_EVT",
  Information: "TIGER_QUERY_SEV_INFO",
};
const QUERY_TYPE_RES: Record<QueryType, string> = {
  "Top Level": "TIGER_QUERY_TYPE_TOP",
  "Complete Listing": "TIGER_QUERY_TYPE_COMP",
  "Number of Occurrences": "TIGER_QUERY_TYPE_OCCUR",
  // Not offered in this dialog's QUERY_TYPES list, but required to satisfy the union.
  "Diagnostic Area": "TIGER_QUERY_TYPE_AREA",
  "Currently Abnormal": "TIGER_QUERY_TYPE_CURR",
  "Diagnostics List": "TIGER_QUERY_TYPE_DIAG",
  Invalid: "TIGER_QUERY_TYPE_INVALID",
};
const FILTER_TYPE_RES: Record<FilterType, string> = {
  Off: "TIGER_QUERY_FILTER_OFF",
  On: "TIGER_QUERY_FILTER_ON",
  Inverse: "TIGER_QUERY_FILTER_INV",
};

const SEVERITIES: Severity[] = [
  "Trip",
  "Critical",
  "Fault",
  "Warning",
  "Event",
  "Information",
];

const QUERY_TYPES: QueryType[] = [
  "Top Level",
  "Complete Listing",
  "Number of Occurrences",
  "Currently Abnormal",
  "Diagnostics List",
  "Invalid",
];

const FILTER_TYPES: FilterType[] = ["Off", "On", "Inverse"];

// "All Turbines" view (turbine === "") only offers this single option.
const ALL_STATES = "All States";

interface DiagnosticsQueryDialogProps extends DialogProps {
  defaultTurbine?: string;
  allTurbines?: TurbineInfo[];
  // The "Trip" severity is only offered when the site enables it
  // (enable_trip_severity in machine_config.ini). Defaults to shown.
  enableTripSeverity?: boolean;
  // The "Diagnostics List" query type is only offered when the site enables it
  // (enable_diag_list_query in machine_config.ini). Defaults to shown.
  enableDiagListQuery?: boolean;
  // The "Invalid" query type is only offered when the site enables it
  // (enable_diag_invalid_query in machine_config.ini). Defaults to shown.
  enableDiagInvalidQuery?: boolean;
  // Each Alarm Sources group is only shown when the site actually acquires that
  // alarm type (get_site_*_alarm). All default to shown.
  siteMarkAlarm?: boolean;
  siteSentinelAlarm?: boolean;
  siteOpcAlarm?: boolean;
  // Site default severity bitmask (get_default_severity_mask). Seeds the
  // severity + alarm-source checkboxes on a fresh window. 0 = not provided
  // (keep the all-on default), which is what standalone/test renders use.
  defaultSeverityMask?: number;
  // When the window was opened via right-click "Display Diagnostics", this is
  // the auto-launched per-tag query. It seeds every field once (so reopening
  // "Specify Query…" shows the tag, Complete Listing, all severities, etc.),
  // taking precedence over persisted settings. Mirrors C++ set_tag_query.
  initialParams?: DiagnosticsQueryParams;
}

// MESSAGE_SEVERITY bits from libTiger/pc_common.h:94.
const SEVERITY_BIT: Record<Severity, number> = {
  Trip: 0x01,
  Critical: 0x02,
  Fault: 0x04,
  Warning: 0x08,
  Event: 0x10,
  Information: 0x20,
};
const MARK_PALARM_BITS = 0x100 | 0x200;
const MARK_DALARM_BITS = 0x400 | 0x800;
const MARK_EVENT_BITS = 0x1000 | 0x2000;
const OPC_ALARM_BITS = 0x4000 | 0x8000;
const OPC_EVENT_BIT = 0x10000;
const SENTINEL_TRIP_BIT = 0x20000;
const SENTINEL_ALARM_BIT = 0x40000;

// All dialog fields, persisted so each Diagnostics window remembers the query
// it was last set to. Stored in sessionStorage (NOT localStorage): sessionStorage
// is scoped to a single Tauri webview, so every Diagnostics window keeps its own
// settings and a freshly-opened window starts with defaults.
interface SavedSettings {
  turbine: string;
  timeRange: TimeRange;
  isHistorical: boolean;
  severity: Record<Severity, boolean>;
  queryType: QueryType;
  filterType: FilterType;
  speedtronicAlarms: { palarm: boolean; dalarm: boolean; event: boolean };
  sentinelAlarms: { trip: boolean; alarm: boolean };
  opcAlarms: { alarm: boolean; event: boolean };
  messageId: string;
  messageText: string;
  messageArea: string;
  tagName: string;
  turbineState: string;
  latchCount: string;
  groupTurbine: boolean;
  groupArea: boolean;
  failedSensor: boolean;
}

const SETTINGS_KEY = "tiger.diagQuery.settings";

function loadSavedSettings(): Partial<SavedSettings> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SETTINGS_KEY);
    return raw ? (JSON.parse(raw) as Partial<SavedSettings>) : null;
  } catch {
    // sessionStorage may be unavailable or hold corrupt JSON; fall back to defaults.
    return null;
  }
}

/// Convert a query (the auto-launched per-tag query) into the dialog's field
/// seeds, so opening "Specify Query…" reflects exactly what was run.
function seedFromParams(p: DiagnosticsQueryParams): SavedSettings {
  const has = (s: Severity) => p.severity.includes(s);
  return {
    turbine: p.turbine,
    timeRange: p.timeRange,
    isHistorical: p.isHistorical,
    severity: {
      Trip: has("Trip"),
      Critical: has("Critical"),
      Fault: has("Fault"),
      Warning: has("Warning"),
      Event: has("Event"),
      Information: has("Information"),
    },
    queryType: p.queryType,
    filterType: p.filterType,
    speedtronicAlarms: p.speedtronicAlarms,
    sentinelAlarms: p.sentinelAlarms,
    opcAlarms: p.opcAlarms,
    messageId: p.messageId,
    messageText: p.messageText,
    messageArea: p.messageArea,
    tagName: p.tagName,
    turbineState: p.turbineState,
    latchCount: p.latchCount,
    groupTurbine: p.groupTurbine,
    groupArea: p.groupArea,
    failedSensor: p.failedSensor,
  };
}

/// Today's local date as "YYYY-MM-DD" (the format an <input type="date"> wants).
function todayIsoDate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function DiagnosticsQueryDialog({
  open,
  onClose,
  onApply,
  defaultTurbine,
  allTurbines,
  enableTripSeverity = true,
  enableDiagListQuery = true,
  enableDiagInvalidQuery = true,
  siteMarkAlarm = true,
  siteSentinelAlarm = true,
  siteOpcAlarm = true,
  defaultSeverityMask = 0,
  initialParams,
}: DiagnosticsQueryDialogProps) {
  const showAnyAlarmSource = siteMarkAlarm || siteSentinelAlarm || siteOpcAlarm;
  // "Trip" drops out of the list entirely when the site hasn't enabled it.
  const visibleSeverities = enableTripSeverity
    ? SEVERITIES
    : SEVERITIES.filter((s) => s !== "Trip");
  // "Diagnostics List" and "Invalid" only appear when the site enables them.
  const visibleQueryTypes = QUERY_TYPES.filter(
    (qt) =>
      (enableDiagListQuery || qt !== "Diagnostics List") &&
      (enableDiagInvalidQuery || qt !== "Invalid"),
  );
  // Read the persisted settings for this window once, on mount.
  const [saved] = useState(loadSavedSettings);
  // A per-tag query (right-click "Display Diagnostics") seeds every field and
  // takes precedence over persisted settings; otherwise fall back to `saved`.
  // Computed once on mount (the dialog stays mounted, so seeds aren't re-run
  // and the user's later edits aren't clobbered).
  const [init] = useState<Partial<SavedSettings> | null>(() =>
    initialParams ? seedFromParams(initialParams) : saved,
  );

  // Seed from the per-tag/saved selection, else the turbine the window was
  // opened for (defaultTurbine, a config_name from the ?turbine= param), else "".
  const [turbine, setTurbine] = useState(init?.turbine ?? defaultTurbine ?? "");
  const [turbines, setTurbines] = useState<TurbineInfo[]>(allTurbines ?? []);

  React.useEffect(() => {
    // If the parent supplies a non-empty turbine list, use it; otherwise fetch
    // (an empty array just means the parent's own fetch hasn't resolved or failed).
    if (allTurbines && allTurbines.length > 0) {
      setTurbines(allTurbines);
      return;
    }
    (async () => {
      setTurbines(await fetchTurbineList());
    })();
  }, [allTurbines]);

  // Resolve the default turbine once the turbine list is known (independent of
  // whether the dialog is open, so the selection is correct the moment it shows):
  //   - if defaultTurbine matches a known turbine → that one (single-turbine view)
  //   - else if there are 2+ turbines → "" (sentinel meaning "all turbines",
  //     i.e. the user was on the site view)
  //   - else if there's exactly one → that one
  // This runs only once (turbineInitialized) so reopening the dialog doesn't clobber
  // the user's selection, and is skipped entirely when settings were restored.
  const turbineInitialized = React.useRef(false);
  React.useEffect(() => {
    if (turbineInitialized.current) return;
    if (turbines.length === 0) return;
    // A single-turbine window (a web `?live=<config>` tab scopes the turbine
    // list to its own config) has no "All Turbines" — clamp the selection to
    // that turbine even over restored settings, so a stale "" can't turn the
    // query site-wide (the daemon fans a site query out to EVERY turbine).
    if (turbines.length === 1) {
      setTurbine(turbines[0].config_name);
      turbineInitialized.current = true;
      return;
    }
    if (init) return;
    const matching = turbines.find((t) => t.config_name === defaultTurbine);
    if (matching) setTurbine(matching.config_name);
    else setTurbine("");
    turbineInitialized.current = true;
  }, [turbines, defaultTurbine, saved]);

  // Load the turbine-state options for the selected turbine. The "All Turbines"
  // view (turbine === "") only offers "All States". After fetching, drop the
  // current selection back to "All States" if it isn't valid for this turbine
  // (validated against the freshly-fetched list so a restored selection isn't
  // wiped before its states have loaded).
  React.useEffect(() => {
    if (!turbine) {
      setTurbineStates([ALL_STATES]);
      setTurbineState((cur) => (cur === ALL_STATES ? cur : ALL_STATES));
      return;
    }
    let cancelled = false;
    (async () => {
      const states = await fetchTurbineStates(turbine);
      if (cancelled) return;
      const list = [ALL_STATES, ...states];
      setTurbineStates(list);
      setTurbineState((cur) => (list.includes(cur) ? cur : ALL_STATES));
    })();
    return () => {
      cancelled = true;
    };
  }, [turbine]);

  const [timeRange, setTimeRange] = useState<TimeRange>(
    init?.timeRange ?? {
      startDate: todayIsoDate(),
      startTime: "",
      endDate: todayIsoDate(),
      endTime: "",
    },
  );
  // Replay does NOT default to a historical query: a non-historical query in
  // replay is served from the replay session (the backend maps its ONLINE source
  // to OFFLINE — C++ diagnostics_display.cpp:2318-2333). The user can still tick
  // Historical for an explicit archived ("QUERY") window in either mode.
  const [isHistorical, setIsHistorical] = useState(init?.isHistorical ?? false);

  const [severity, setSeverity] = useState<Record<Severity, boolean>>(
    init?.severity ?? {
      Trip: true,
      Critical: true,
      Fault: true,
      Warning: true,
      Event: true,
      Information: true,
    },
  );

  const [queryType, setQueryType] = useState<QueryType>(init?.queryType ?? "Top Level");
  // A restored selection of a now-hidden query type ("Diagnostics List" or
  // "Invalid") is no longer valid once the site disables it; fall back to
  // "Top Level".
  React.useEffect(() => {
    if (
      (!enableDiagListQuery && queryType === "Diagnostics List") ||
      (!enableDiagInvalidQuery && queryType === "Invalid")
    ) {
      setQueryType("Top Level");
    }
  }, [enableDiagListQuery, enableDiagInvalidQuery, queryType]);
  const [filterType, setFilterType] = useState<FilterType>(init?.filterType ?? "Off");

  const [speedtronicAlarms, setSpeedtronicAlarms] = useState(
    init?.speedtronicAlarms ?? {
      palarm: false,
      dalarm: false,
      event: false,
    },
  );

  const [sentinelAlarms, setSentinelAlarms] = useState(
    init?.sentinelAlarms ?? {
      trip: false,
      alarm: false,
    },
  );

  const [opcAlarms, setOpcAlarms] = useState(
    init?.opcAlarms ?? {
      alarm: false,
      event: false,
    },
  );

  const [messageId, setMessageId] = useState(init?.messageId ?? "");
  const [messageText, setMessageText] = useState(init?.messageText ?? "");
  const [messageArea, setMessageArea] = useState(init?.messageArea ?? "");
  const [tagName, setTagName] = useState(init?.tagName ?? "");
  const [turbineState, setTurbineState] = useState(init?.turbineState ?? ALL_STATES);
  // Turbine-state options for the currently-selected turbine. Defaults to just
  // "All States" until the per-turbine list is fetched (and stays that way for
  // the "All Turbines" view).
  const [turbineStates, setTurbineStates] = useState<string[]>([ALL_STATES]);
  const [latchCount, setLatchCount] = useState(init?.latchCount ?? "");

  const [groupTurbine, setGroupTurbine] = useState(init?.groupTurbine ?? false);
  const [groupArea, setGroupArea] = useState(init?.groupArea ?? false);
  const [failedSensor, setFailedSensor] = useState(init?.failedSensor ?? false);

  // "Group results per turbine" only makes sense across all turbines (the ""
  // sentinel). Clear it whenever a single turbine is selected, and also when the
  // site has a single config — there the checkbox is hidden, so restored settings
  // or a loaded tool set must not leave it silently active.
  const isAllTurbines = turbine === "";
  const canGroupTurbine = isAllTurbines && turbines.length > 1;
  React.useEffect(() => {
    if (!canGroupTurbine && groupTurbine) setGroupTurbine(false);
  }, [canGroupTurbine, groupTurbine]);

  // Persist every field change to this window's sessionStorage so the dialog
  // reopens exactly as the user left it.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const settings: SavedSettings = {
      turbine,
      timeRange,
      isHistorical,
      severity,
      queryType,
      filterType,
      speedtronicAlarms,
      sentinelAlarms,
      opcAlarms,
      messageId,
      messageText,
      messageArea,
      tagName,
      turbineState,
      latchCount,
      groupTurbine,
      groupArea,
      failedSensor,
    };
    try {
      window.sessionStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // sessionStorage may be unavailable (e.g. private mode); ignore.
    }
  }, [
    turbine,
    timeRange,
    isHistorical,
    severity,
    queryType,
    filterType,
    speedtronicAlarms,
    sentinelAlarms,
    opcAlarms,
    messageId,
    messageText,
    messageArea,
    tagName,
    turbineState,
    latchCount,
    groupTurbine,
    groupArea,
    failedSensor,
  ]);

  // Seed the severity + alarm-source checkboxes from the site default mask the
  // first time it arrives — but only for a fresh window (no restored settings),
  // and only once so it never clobbers the user's own edits.
  const maskApplied = React.useRef(false);
  React.useEffect(() => {
    if (init) return;
    if (maskApplied.current) return;
    if (!defaultSeverityMask) return;
    maskApplied.current = true;
    const m = defaultSeverityMask;
    setSeverity({
      Trip: (m & SEVERITY_BIT.Trip) !== 0,
      Critical: (m & SEVERITY_BIT.Critical) !== 0,
      Fault: (m & SEVERITY_BIT.Fault) !== 0,
      Warning: (m & SEVERITY_BIT.Warning) !== 0,
      Event: (m & SEVERITY_BIT.Event) !== 0,
      Information: (m & SEVERITY_BIT.Information) !== 0,
    });
    setSpeedtronicAlarms({
      palarm: (m & MARK_PALARM_BITS) !== 0,
      dalarm: (m & MARK_DALARM_BITS) !== 0,
      event: (m & MARK_EVENT_BITS) !== 0,
    });
    setSentinelAlarms({
      trip: (m & SENTINEL_TRIP_BIT) !== 0,
      alarm: (m & SENTINEL_ALARM_BIT) !== 0,
    });
    setOpcAlarms({
      alarm: (m & OPC_ALARM_BITS) !== 0,
      event: (m & OPC_EVENT_BIT) !== 0,
    });
  }, [defaultSeverityMask, saved]);

  const buildParams = (): DiagnosticsQueryParams => ({
    turbine,
    severity: visibleSeverities.filter((s) => severity[s]),
    queryType,
    filterType,
    messageId,
    messageText,
    messageArea,
    tagName,
    latchCount,
    turbineState,
    groupTurbine,
    groupArea,
    failedSensor,
    isHistorical,
    timeRange,
    speedtronicAlarms,
    sentinelAlarms: { trip: sentinelAlarms.trip, alarm: sentinelAlarms.alarm },
    opcAlarms: { alarm: opcAlarms.alarm, event: opcAlarms.event },
  });

  // Historical queries carry a start/end window; enforce end >= start before applying.
  const rangeError = (): string | null => {
    if (!isHistorical) return null;
    const { startDate, startTime, endDate, endTime } = timeRange;
    return startDate && endDate
      ? validateEndAfterStart(
          `${startDate}T${startTime || "00:00:00"}`,
          `${endDate}T${endTime || "00:00:00"}`,
        )
      : null;
  };
  const handleApply = () => {
    const err = rangeError();
    if (err) {
      alert(err);
      return;
    }
    onApply?.(buildParams());
  };
  const handleApplyAll = () => {
    const err = rangeError();
    if (err) {
      alert(err);
      return;
    }
    onApply?.({ ...buildParams(), applyAll: true });
  };

  const toggleSeverity = (s: Severity) =>
    setSeverity((prev) => ({ ...prev, [s]: !prev[s] }));

  if (!open) return null;

  return (
    <div
      style={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={getTigerResource("TIGER_DIAG_MSG_TITLE")}
    >
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{getTigerResource("TIGER_DIAG_MSG_TITLE")}</span>
          <button className="acrylic-close-btn" onClick={onClose}>
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Turbine */}
          <div style={styles.section}>
            <div style={styles.card}>
              <div style={styles.fieldGroup}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_TURBINE")}</label>
                  <select
                    style={styles.select}
                    value={turbine}
                    onChange={(e) => setTurbine(e.target.value)}
                  >
                    {turbines.length > 1 && (
                      <option value="">{getTigerResource("TIGER_QUERY_ALL_TURBINES")}</option>
                    )}
                    {turbines.map((t) => (
                      <option key={t.config_name} value={t.config_name}>
                        {t.turbine_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_TURBINE_STATE")}</label>
                  <select
                    style={styles.select}
                    value={turbineState}
                    onChange={(e) => setTurbineState(e.target.value)}
                  >
                    {turbineStates.map((ts) => (
                      <option key={ts} value={ts}>
                        {/* ALL_STATES is an internal sentinel value (compared in
                            diagQueryParamsBridge etc.); localize display only. */}
                        {ts === ALL_STATES ? getTigerResource("TIGER_QUERY_ALL_STATES") : ts}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.checkColumn}>
                  {/* Grouping per turbine is meaningless with a single config
                      (there is no "All Turbines" option to select), so drop the
                      checkbox entirely rather than show a permanently-disabled one. */}
                  {turbines.length > 1 && (
                    <label
                      style={{
                        ...styles.checkboxLabel,
                        ...(isAllTurbines
                          ? {}
                          : { color: acrylicTokens.textMuted, cursor: "not-allowed" }),
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={groupTurbine}
                        disabled={!isAllTurbines}
                        onChange={(e) => setGroupTurbine(e.target.checked)}
                      />
                      {getTigerResource("TIGER_QUERY_GROUP")}
                    </label>
                  )}
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={groupArea}
                      onChange={(e) => setGroupArea(e.target.checked)}
                    />
                    {getTigerResource("TIGER_QUERY_TYPE_AREA")}
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Time Selection */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_QUERY_TIME")}</span>
            <div style={styles.card}>
              <div style={styles.fieldGroup}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_START_DATE")}</label>
                  <LangDateInput
                    style={isHistorical ? styles.input : styles.inputDisabled}
                    value={timeRange.startDate}
                    disabled={!isHistorical}
                    onChange={(iso) =>
                      setTimeRange((prev) => ({ ...prev, startDate: iso }))
                    }
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_END_DATE")}</label>
                  <LangDateInput
                    style={isHistorical ? styles.input : styles.inputDisabled}
                    value={timeRange.endDate}
                    disabled={!isHistorical}
                    onChange={(iso) =>
                      setTimeRange((prev) => ({ ...prev, endDate: iso }))
                    }
                  />
                </div>
              </div>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isHistorical}
                  onChange={(e) => setIsHistorical(e.target.checked)}
                />
                {getTigerResource("TIGER_CHANNEL_HIST")}
              </label>
            </div>
          </div>

          {/* Severity + Query Type, side by side */}
          <div style={styles.sideBySide}>
            {/* Severity */}
            <div style={{ ...styles.section, flex: 1 }}>
              <span style={styles.sectionLabel}>{getTigerResource("TIGER_QUERY_SEVERITY")}</span>
              <div style={{ ...styles.card, flex: 1 }}>
                <div style={styles.checkColumn}>
                  {visibleSeverities.map((s) => (
                    <label key={s} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={severity[s]}
                        onChange={() => toggleSeverity(s)}
                      />
                      {getTigerResource(SEVERITY_RES[s])}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Query Type */}
            <div style={{ ...styles.section, flex: 1 }}>
              <span style={styles.sectionLabel}>{getTigerResource("TIGER_QUERY_TYPE")}</span>
              <div style={{ ...styles.card, flex: 1 }}>
                <div style={styles.checkColumn}>
                  {visibleQueryTypes.map((qt) => (
                    <label key={qt} style={styles.checkboxLabel}>
                      <input
                        type="radio"
                        name="queryType"
                        checked={queryType === qt}
                        onChange={() => setQueryType(qt)}
                      />
                      {getTigerResource(QUERY_TYPE_RES[qt])}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Filter */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_QUERY_FILTER")}</span>
            <div style={styles.card}>
              <div style={styles.checkRow}>
                {FILTER_TYPES.map((ft) => (
                  <label key={ft} style={styles.checkboxLabel}>
                    <input
                      type="radio"
                      name="filterType"
                      checked={filterType === ft}
                      onChange={() => setFilterType(ft)}
                    />
                    {getTigerResource(FILTER_TYPE_RES[ft])}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Alarm Sources */}
          {showAnyAlarmSource && (
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_QUERY_ALARM_SOURCES")}</span>
            <div style={{ ...styles.fieldGroup, alignItems: "flex-start" }}>
              {siteMarkAlarm && (
              <div style={{ ...styles.card, flex: 1 }}>
                <div style={styles.cardTitle}>{getTigerResource("TIGER_QUERY_ALARM")}</div>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={speedtronicAlarms.palarm}
                    onChange={(e) =>
                      setSpeedtronicAlarms((prev) => ({ ...prev, palarm: e.target.checked }))
                    }
                  />
                  {getTigerResource("TIGER_QUERY_ALARM_PALARM")}
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={speedtronicAlarms.dalarm}
                    onChange={(e) =>
                      setSpeedtronicAlarms((prev) => ({ ...prev, dalarm: e.target.checked }))
                    }
                  />
                  {getTigerResource("TIGER_QUERY_ALARM_DALARM")}
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={speedtronicAlarms.event}
                    onChange={(e) =>
                      setSpeedtronicAlarms((prev) => ({ ...prev, event: e.target.checked }))
                    }
                  />
                  {getTigerResource("TIGER_QUERY_ALARM_EVT")}
                </label>
              </div>
              )}
              {siteSentinelAlarm && (
              <div style={{ ...styles.card, flex: 1 }}>
                <div style={styles.cardTitle}>{getTigerResource("TIGER_SENTINEL_ALARM")}</div>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={sentinelAlarms.trip}
                    onChange={(e) =>
                      setSentinelAlarms((prev) => ({ ...prev, trip: e.target.checked }))
                    }
                  />
                  {getTigerResource("TIGER_SENT_TRIPS")}
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={sentinelAlarms.alarm}
                    onChange={(e) =>
                      setSentinelAlarms((prev) => ({ ...prev, alarm: e.target.checked }))
                    }
                  />
                  {getTigerResource("TIGER_SENT_ALARMS")}
                </label>
              </div>
              )}
              {siteOpcAlarm && (
              <div style={{ ...styles.card, flex: 1 }}>
                <div style={styles.cardTitle}>{getTigerResource("TIGER_OPC_ALARM")}</div>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={opcAlarms.alarm}
                    onChange={(e) =>
                      setOpcAlarms((prev) => ({ ...prev, alarm: e.target.checked }))
                    }
                  />
                  {getTigerResource("TIGER_OPC_ALARMS")}
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={opcAlarms.event}
                    onChange={(e) =>
                      setOpcAlarms((prev) => ({ ...prev, event: e.target.checked }))
                    }
                  />
                  {getTigerResource("TIGER_OPC_EVENTS")}
                </label>
              </div>
              )}
            </div>
          </div>
          )}

          {/* Pattern Matching */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_CHANNEL_PATTERN")}</span>
            <div style={styles.card}>
              <div style={styles.fieldGroup}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_QUERY_MES_ID")}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={messageId}
                    onChange={(e) => setMessageId(e.target.value)}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_QUERY_MES_TXT")}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                  />
                </div>
              </div>
              <div style={styles.fieldGroup}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_QUERY_MES_AREA")}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={messageArea}
                    onChange={(e) => setMessageArea(e.target.value)}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_GRAPH_TAGNAME")}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={tagName}
                    onChange={(e) => setTagName(e.target.value)}
                  />
                </div>
              </div>
              <div style={styles.fieldGroup}>
                <div style={styles.fieldNarrow}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_QUERY_LATCH")}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={latchCount}
                    onChange={(e) => setLatchCount(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Failed sensors (standalone option, not grouping) */}
          <div style={styles.section}>
            <div style={styles.card}>
              <div style={styles.checkRow}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={failedSensor}
                    onChange={(e) => setFailedSensor(e.target.checked)}
                  />
                  {getTigerResource("TIGER_QUERY_FAILED_SENSOR")}
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button className="btn-acrylic-primary" onClick={handleApply}>
            {getTigerResource("TIGER_RES_APPLY")}
          </button>
          <button className="btn-acrylic-secondary" onClick={handleApplyAll}>
            {getTigerResource("TIGER_RES_APPLY_ALL")}
          </button>
          <button className="btn-acrylic-secondary" onClick={onClose}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  dialog: acrylicDialogSurface({ width: 620, height: 720, minWidth: 480, minHeight: 640 }),
  // Bespoke keys not provided by acrylicStyles.
  cardTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: acrylicTokens.textSecondary,
  },
  inputDisabled: {
    flex: 1,
    fontSize: 13,
    padding: "7px 10px",
    borderRadius: 7,
    border: acrylicTokens.controlBorder,
    background: "rgba(255,255,255,0.55)",
    color: acrylicTokens.textMuted,
    outline: "none",
    fontFamily: "inherit",
    cursor: "not-allowed" as const,
  },
  checkRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "8px 16px",
    alignItems: "center",
  },
  checkColumn: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 8,
  },
  sideBySide: {
    display: "flex",
    gap: 12,
    alignItems: "stretch",
  },
  checkboxLabel: {
    fontSize: 13,
    color: acrylicTokens.textSecondary,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
  },
} as const;
